import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { ActionIcon, Badge, Group, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import { fuzzyFilterStringList } from "../lib/catalogFuzzyFilter";
import { axioTokens } from "../theme";

const SUGGESTION_CLOSE_DELAY_MS = 150;
const SUGGESTION_LIMIT = 12;

type Props = {
  label?: string;
  description?: string;
  placeholder?: string;
  value: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  disabled?: boolean;
  required?: boolean;
  error?: React.ReactNode;
  maxTags?: number;
  size?: string;
};

/**
 * Crash-proof tag field. Do not use Mantine TagsInput for large fuzzy catalogs —
 * optionsLockup[val].value throws when suggestions reshuffle while typing.
 */
export function SkillTagsField({
  label = "Skills",
  description,
  placeholder = "Search or type — comma / Enter to add",
  value,
  onChange,
  suggestions = [],
  disabled,
  required,
  error,
  maxTags = 40,
  size = "md",
}: Props) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const picked = new Set(value.map((v) => v.toLowerCase()));
    const ranked = draft.trim()
      ? fuzzyFilterStringList(suggestions, draft, SUGGESTION_LIMIT)
      : suggestions.slice(0, SUGGESTION_LIMIT);
    return ranked.filter((s) => !picked.has(s.toLowerCase()));
  }, [suggestions, draft, value]);

  function updateDropdownPosition() {
    if (!inputRef.current) return;
    const box = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: box.bottom + axioTokens.unit[4],
      left: box.left,
      width: box.width,
      zIndex: axioTokens.number.overlay,
    });
  }

  useLayoutEffect(() => {
    if (!open) return;
    updateDropdownPosition();
  }, [open, draft, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updateDropdownPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open]);

  function addRaw(rawInput: string) {
    const parts = rawInput.split(/[,;]/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...value];
    for (const part of parts) {
      if (next.length >= maxTags) break;
      if (next.some((v) => v.toLowerCase() === part.toLowerCase())) continue;
      next.push(part);
    }
    onChange(next);
    setDraft("");
    setOpen(false);
    inputRef.current?.focus();
  }

  function onDraftChange(next: string) {
    if (/[,;]/.test(next)) {
      const parts = next.split(/[,;]/);
      const toAdd = parts.slice(0, -1).join(",");
      const rest = parts[parts.length - 1] ?? "";
      if (toAdd.trim()) addRaw(toAdd);
      setDraft(rest);
      setOpen(true);
      return;
    }
    setDraft(next);
    setOpen(true);
  }

  return (
    <Stack gap="xs">
      <TextInput
        ref={inputRef}
        label={label}
        withAsterisk={required}
        description={description}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        size={size}
        value={draft}
        rightSection={
          <ActionIcon
            variant="subtle"
            color="violet"
            size="sm"
            disabled={disabled || !draft.trim()}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => addRaw(draft)}
            aria-label="Add"
          >
            <IconPlus size="var(--axio-unit-16)" />
          </ActionIcon>
        }
        onChange={(e) => onDraftChange(e.currentTarget.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), SUGGESTION_CLOSE_DELAY_MS)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (draft.trim()) addRaw(draft);
          }
        }}
      />
      {open && !disabled && filtered.length > 0 &&
        createPortal(
          <Stack
            gap="var(--axio-unit-0)"
            style={{
              ...dropdownStyle,
              maxHeight: "var(--axio-unit-220)",
              overflowY: "auto",
              background: "var(--mantine-color-body)",
              border: "var(--axio-unit-1) solid var(--axio-color-border)",
              borderRadius: "var(--axio-radius)",
              boxShadow: "var(--axio-shadow-popover)",
            }}
          >
            {filtered.map((s) => (
              <UnstyledButton
                key={s}
                px="sm"
                py="var(--axio-unit-8)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addRaw(s);
                }}
                style={{ textAlign: "left", width: "var(--axio-percentage-full)" }}
              >
                <Text size="sm">{s}</Text>
              </UnstyledButton>
            ))}
          </Stack>,
          document.body,
        )}
      {value.length > 0 && (
        <Group gap="xs">
          {value.map((tag) => (
            <Badge
              key={tag}
              size="lg"
              variant="light"
              color="violet"
              rightSection={
                !disabled ? (
                  <ActionIcon
                    size="xs"
                    variant="transparent"
                    c="inherit"
                    onClick={() => onChange(value.filter((t) => t !== tag))}
                    aria-label={`Remove ${tag}`}
                  >
                    <IconX size="var(--axio-unit-12)" />
                  </ActionIcon>
                ) : undefined
              }
            >
              {tag}
            </Badge>
          ))}
        </Group>
      )}
    </Stack>
  );
}
